import type { Form } from '../models/Form';
import type { Step } from '../models/Step';
import type { Section } from '../models/Section';
import type { Group } from '../models/Group';
import type { Question } from '../models/Question';
import type { IDisplayService } from './displayService';
import type { IValidationService } from './validationService';
import type { FormService } from './formService';

interface SummarizedQuestion {
  type: string;
  id: string;
  label: string;
  answer: unknown;
}

interface SummarizedGroup {
  id: string;
  label: string;
  index: number;
  questions: SummarizedQuestion[];
}

interface SummarizedSection {
  id: string;
  label: string;
  groups: SummarizedGroup[];
}

interface SummarizedStep {
  id: string;
  label: string;
  title: string;
  sections: SummarizedSection[];
}

interface SummarizedForm {
  guid: string;
  steps: SummarizedStep[];
  declaration: Form['declaration'];
  acceptPaymentTermsAndConditions: boolean;
}

export class DataSummarizer {
  constructor(
    private formService: FormService,
    private displayService: IDisplayService,
    private validationService: IValidationService
  ) {}

  public summarize(form: Form): { data: SummarizedForm } {
    const summarizedForm = this.createForm(form);

    form.steps.forEach((step: Step) => {
      const summarizedStep = this.createStep(step);

      step.sections.forEach((section: Section) => {
        if (!this.displayService.showSection(section)) {
          return;
        }

        const summarizedSection = this.createSection(section);

        section.groups.forEach((group: Group) => {
          if (!this.displayService.showGroup(group)) {
            return;
          }

          const summarizedGroup = this.createGroup(group);

          group.questions.forEach((question: Question) => {
            if (!this.displayService.showQuestion(question, group) || !question.hasPopulatedAnswer()) {
              return;
            }

            summarizedGroup.questions.push(this.createQuestion(question));
          });

          if (summarizedGroup.questions.length > 0) {
            (summarizedSection.groups as SummarizedGroup[]).push(summarizedGroup);
          }
        });

        if ((summarizedSection.groups as SummarizedGroup[]).length > 0) {
          (summarizedStep.sections as SummarizedSection[]).push(summarizedSection);
        }
      });

      if ((summarizedStep.sections as SummarizedSection[]).length > 0) {
        (summarizedForm.steps as SummarizedStep[]).push(summarizedStep);
      }
    });

    return {
      data: summarizedForm,
    };
  }

  private createForm(form: Form): SummarizedForm {
    return {
      guid: form.guid,
      steps: [],
      declaration: form.declaration,
      acceptPaymentTermsAndConditions: form.acceptPaymentTermsAndConditions,
    };
  }

  private createStep(step: Step): SummarizedStep {
    return {
      id: step.id,
      label: step.label,
      title: step.title,
      sections: [],
    };
  }

  private createSection(section: Section): SummarizedSection {
    return {
      id: section.id,
      label: section.label,
      groups: [],
    };
  }

  private createGroup(group: Group): SummarizedGroup {
    return {
      id: group.id,
      label: group.label,
      index: group.index,
      questions: [],
    };
  }

  private createQuestion(question: Question): SummarizedQuestion {
    return {
      type: question.type,
      id: question.id,
      label: question.label,
      answer: question.answer,
    };
  }
}
